/**
 * Rounding — the single most consequential 30 lines in the system.
 *
 * SCOPE §18.1-1 specifies "round half-up". Applied to money that has to mean
 * **half away from zero**, not half toward positive infinity:
 *
 *   sale line rounds  $10.005 -> $10.01
 *   its refund must be         -> -$10.01
 *
 * With naive half-up, -10.005 rounds to -10.00 and the refund fails to reverse
 * the sale by a cent. Multiply that across a season of returns and EOD stops
 * tying to the bank deposit — the exact failure mode §17 calls out as trust-
 * destroying. Half-away-from-zero is identical to half-up for every positive
 * amount (which is every price, tax, and total), and symmetric for reversals.
 *
 * All intermediate arithmetic runs in bigint. A subtotal in cents times a rate
 * in ppm is ~1e14, which Number happens to survive today, but "happens to
 * survive" is not a property we want load-bearing under money.
 */

/**
 * Integer division of `numerator / denominator`, rounding halves away from zero.
 * Exact — no floating point is involved at any step.
 */
export function divideRounded(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) {
    throw new RangeError('divideRounded: denominator must be non-zero');
  }

  // Normalise the sign onto the numerator so the denominator is always positive.
  const d = denominator < 0n ? -denominator : denominator;
  const n = denominator < 0n ? -numerator : numerator;

  const negative = n < 0n;
  const magnitude = negative ? -n : n;

  // floor((magnitude / d) + 1/2), computed without division of fractions.
  const quotient = (2n * magnitude + d) / (2n * d);

  return negative ? -quotient : quotient;
}

/**
 * Multiply `value` by the fraction `numerator/denominator`, rounding to an
 * integer with {@link divideRounded}.
 */
/**
 * A figure rounded to a whole number, halves away from zero — 2.5 → 3 and
 * −2.5 → −3, the same rule money follows — for a report shown in whole
 * dollars and whole percents (decision 218). Never used to store anything.
 */
export function roundHalfAwayFromZero(value: number): number {
  const rounded = Math.round(Math.abs(value));
  return rounded === 0 ? 0 : Math.sign(value) * rounded;
}

/** Cents as whole dollars, halves away from zero: 12350 → 124, −12350 → −124. */
export function wholeDollarsOf(value: number): number {
  return roundHalfAwayFromZero(value / 100);
}

export function multiplyRounded(value: bigint, numerator: bigint, denominator: bigint): bigint {
  return divideRounded(value * numerator, denominator);
}

/** Truncate `numerator / denominator` toward zero. Used by allocation, never by pricing. */
export function divideTruncated(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) {
    throw new RangeError('divideTruncated: denominator must be non-zero');
  }
  return numerator / denominator; // BigInt division already truncates toward zero.
}
